import { useState } from 'react';
import { SetValueConfig } from 'react-hook-form';
import { fetchCopyAssistSuggestionsQuery } from 'api/queries';
import { useLazyGraphql } from 'utils/hooks/useLazyGraphql';
import { MediumDataKey } from 'modules/templateEditor/common/types';
import { SelectedMessageMedium } from 'modules/templateEditor/common';
import { CopyAssistTagNames } from 'modules/domains/copyAssist/copyAssist';
import { CopyAssistButton } from 'modules/ai/copyAssist/components/CopyAssistButton';
import { getMuseContentType } from 'modules/ai/copyAssist/utils';
import {
  SUGGESTION_OPTION_COUNT,
  SUGGESTION_OPTION_MAX_CHARACTERS,
  MIN_CHARACTER_COUNT,
} from 'modules/ai/copyAssist/constants';
import { RUMHelpers } from 'services/observability/datadog';
import { generateActionName } from 'modules/ai/copyAssist/observabilityUtils';
import { CopyAssistDropdownWithSelectionHighlight } from './components/CopyAssistDropdownWithSelectionHighlight';

interface CommonCopyAssistButtonDropdownProps {
  value: string;
  /* ref for the element to target */
  targetRef?: React.RefObject<HTMLDivElement> | null;
  targetTagName: CopyAssistTagNames;
  /* Medium such as Email, SMS, Push, InApp */
  messageMedium: SelectedMessageMedium | null;
  /* The experiment module would use this to add all copy assist suggestions as variants */
  addSuggestionsAsVariants?: boolean;
  maxLength?: number;
}

interface CopyAssistButtonDropdownPropsWithMediumDataKey
  extends CommonCopyAssistButtonDropdownProps {
  mediumDataKey: MediumDataKey;
  /* function called to set the field for the template */
  setCurrentMediumField: (fieldName: MediumDataKey, value: any) => void;
}

interface CopyAssistButtonDropdownPropsWithFieldName extends CommonCopyAssistButtonDropdownProps {
  fieldName: string;
  /* the setValue method from the RHF's useFormContext  */
  setField: (fieldName: string, value: any, options?: SetValueConfig) => void;
  contentType: string;
}

type CopyAssistButtonDropdownProps =
  | CopyAssistButtonDropdownPropsWithMediumDataKey
  | CopyAssistButtonDropdownPropsWithFieldName;

export const CopyAssistButtonDropdown = (props: CopyAssistButtonDropdownProps) => {
  const {
    value,
    targetRef,
    targetTagName,
    messageMedium,
    addSuggestionsAsVariants = false,
    maxLength = SUGGESTION_OPTION_MAX_CHARACTERS,
  } = props;
  const hasMediumDataKey = 'mediumDataKey' in props;
  const contentType = hasMediumDataKey ? props.mediumDataKey : props.contentType;
  const editFormFieldProps = hasMediumDataKey
    ? {
        mediumDataKey: props.mediumDataKey,
        setCurrentMediumField: props.setCurrentMediumField,
      }
    : {
        fieldName: props.fieldName,
        setField: props.setField,
      };

  const [isDropdownDisplayed, setIsDropdownDisplayed] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const museContentType = getMuseContentType(messageMedium, contentType);

  const { fetch, data, isLoading, error } = useLazyGraphql(fetchCopyAssistSuggestionsQuery, {
    fetchPolicy: 'cache-and-network' as const,
  });

  const onIterateClick = () => {
    RUMHelpers.addRUMAction(generateActionName('Iterate Click'));
    setIsDropdownDisplayed(true);
    fetch({
      count: SUGGESTION_OPTION_COUNT,
      contentType: museContentType,
      maxLength,
      content: value,
    });
  };

  const onResetBtnClick = () => {
    RUMHelpers.addRUMAction(generateActionName('Iterate Reset Click'));
    setSelectedOption('');
    fetch({
      count: SUGGESTION_OPTION_COUNT,
      contentType: museContentType,
      maxLength,
      content: value,
    });
  };

  // Don't display copy suggestion button if not valid messageMedium and mediumDatKey
  if (!museContentType || (value?.length ?? 0) < MIN_CHARACTER_COUNT) {
    return null;
  }

  if (isDropdownDisplayed) {
    return (
      <CopyAssistDropdownWithSelectionHighlight
        {...editFormFieldProps}
        targetRef={targetRef}
        value={value}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        loading={isLoading}
        onResetBtnClick={onResetBtnClick}
        isError={error}
        generatedOptions={data?.museContentAssistSuggestions?.suggestions || []}
        addSuggestionsAsVariants={addSuggestionsAsVariants}
      />
    );
  } else {
    return (
      <CopyAssistButton
        targetRef={targetRef}
        onClick={onIterateClick}
        targetTagName={targetTagName}
      />
    );
  }
};
